import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { register, registerBusiness } from '../services/api';
import FoundingMemberCounter from './FoundingMemberCounter';
import { trackEvent } from '../services/analytics';
import { CATEGORY_OPTIONS, formatCategory, MAX_CATEGORIES_PER_BUSINESS } from '../constants/categories';
import { BATON_ROUGE_METRO } from '../constants/metros';
import { TIER_LIMITS, TIER_NAMES } from '../constants/tierLimits';
import { classifyFootprintError, laneReasonText, failingPairLabel, FailingPair } from '../utils/footprintErrors';
import ReCAPTCHA from 'react-google-recaptcha';

const Register: React.FC = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = '@media (max-width: 768px) { .register-left { display: none !important; } .register-right { padding: 24px 16px !important; width: 100% !important; } }';
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => { const handler = () => setIsMobile(window.innerWidth <= 768); window.addEventListener('resize', handler); return () => window.removeEventListener('resize', handler); }, []);
  const [searchParams] = useSearchParams();
  const [userType, setUserType] = useState<'customer' | 'business'>(
    searchParams.get('type') === 'business' ? 'business' : 'customer'
  );
  // C2: tier comes from /pricing CTA via ?tier=. Whitelist-validate so a
  // bad URL param falls back to no preselection (user picks via inline
  // 3-button picker). Customer signups don't read this.
  const tierParam = searchParams.get('tier');
  const initialTier: 'starter' | 'pro' | 'premium' | null =
    tierParam === 'starter' || tierParam === 'pro' || tierParam === 'premium' ? tierParam : null;
  const [tier, setTier] = useState<'starter' | 'pro' | 'premium' | null>(initialTier);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  // F4-a: empty by default — contractors actively select their launch cities
  // within the limits of the selected tier (was previously all-15-preselected).
  const [serviceCities, setServiceCities] = useState<string[]>([]);
  const [waitlistedEmail, setWaitlistedEmail] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    business_name: '',
    address: '',
    city: '',
    state: 'LA',
    zip: '',
    description: '',
  });
  const [error, setError] = useState('');
  // Plan-limit / lane errors from the backend footprint contracts render
  // distinctly from the generic red error box (see footprintErrors).
  const [planLimitMsg, setPlanLimitMsg] = useState<string | null>(null);
  const [lanePairs, setLanePairs] = useState<FailingPair[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // F4-a: chip limits are driven by the SELECTED tier. Until a tier is picked
  // (tier === null), chips are selectable up to the absolute UI ceiling and
  // submit stays disabled anyway (tier is required to submit).
  const maxCategories = tier ? TIER_LIMITS[tier].maxCategories : MAX_CATEGORIES_PER_BUSINESS;
  const maxCities = tier ? TIER_LIMITS[tier].maxCities : BATON_ROUGE_METRO.length;

  // Over-limit happens only on a downward tier switch with selections already
  // made. We never silently truncate — selections are preserved, a warning is
  // shown, and submit is disabled until the user reduces the selection.
  const categoriesOverLimit = categories.length > maxCategories;
  const citiesOverLimit = serviceCities.length > maxCities;
  const footprintOverLimit = categoriesOverLimit || citiesOverLimit;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleCategory = (cat: string) => {
    if (categories.includes(cat)) {
      setCategories(categories.filter(c => c !== cat));
    } else if (categories.length < maxCategories) {
      setCategories([...categories, cat]);
    }
    // Silently ignore clicks beyond the tier limit; chip is visually disabled
  };

  const toggleServiceCity = (city: string) => {
    if (serviceCities.includes(city)) {
      setServiceCities(serviceCities.filter(c => c !== city));
    } else if (serviceCities.length < maxCities) {
      setServiceCities([...serviceCities, city]);
    }
    // Silently ignore clicks beyond the tier limit; chip is visually disabled
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPlanLimitMsg(null);
    setLanePairs(null);

    if (userType === 'business' && categories.length === 0) {
      setError('Please select at least one service category.');
      return;
    }

    if (userType === 'business' && serviceCities.length === 0) {
      setError('Please select at least one city you serve.');
      return;
    }

    // Never submit a footprint that exceeds the selected tier and rely on the
    // backend to reject it — block client-side and prompt the user to reduce.
    if (userType === 'business' && footprintOverLimit) {
      setError('Your selection exceeds your plan limits. Reduce your categories or cities to continue.');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (userType === 'customer') {
        response = await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          user_type: 'customer',
          phone: formData.phone,
          sms_consent: smsConsent,
          captchaToken,
        });
      } else {
        response = await registerBusiness({
          ...formData,
          sms_consent: smsConsent,
          captchaToken,
          categories,
          service_cities: serviceCities,
          tier,
        });
      }
      if (response.data.waitlisted === true) {
        setWaitlistedEmail(response.data.email || formData.email);
        return;
      }
      trackEvent('CompleteRegistration', { content_category: userType });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (userType === 'customer') {
        navigate('/new-request');
      } else {
        // Route on the SELECTED tier state (the same value sent in the register
        // payload above) — never the raw ?tier= URL param. A user who picked
        // Pro/Premium via the in-form picker has no ?tier= and must still be
        // sent into the gated checkout funnel (?checkout=1 auto-opens Stripe).
        if (tier === 'pro' || tier === 'premium') {
          navigate(`/pricing?tier=${tier}&checkout=1`);
        } else {
          navigate('/requests');
        }
      }
    } catch (err: any) {
      const footprint = classifyFootprintError(err);
      if (footprint?.kind === 'plan-limit') {
        setPlanLimitMsg(footprint.message);
      } else if (footprint?.kind === 'lanes') {
        setLanePairs(footprint.pairs);
      } else {
        setError(err.response?.data?.error || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const businessBenefits = [
    '⚡ See new jobs before competitors',
    '🎯 AI matches you to relevant work only',
    '💵 Flat monthly plan — no per-lead fees',
    '📈 Seat limits mean fewer competing bidders',
    '🆓 Starter plan free forever',
  ];

  const customerBenefits = [
    '🆓 Always free for homeowners',
    '💰 Compare sealed bids from multiple local pros',
    '⚡ Local pros are alerted as soon as you post.',
    '🎯 AI matches you to vetted pros',
    '✅ No pressure, no obligations',
  ];

  if (waitlistedEmail) {
    return (
      <div style={styles.page}>
        <div style={{...styles.leftPanel, display: isMobile ? 'none' : 'flex'}} className="register-left">
          <div style={styles.leftContent}>
            <Link to="/" style={styles.backLink}>← Back to home</Link>
            <div style={styles.leftBadge}>🎉 You're on the list</div>
            <h1 style={styles.leftTitle}>Thanks for your interest in QuoteBid.</h1>
            <p style={styles.leftSubtitle}>
              We're growing market by market. We'll let you know the moment we open in your area.
            </p>
          </div>
        </div>
        <div style={styles.rightPanel} className="register-right">
          <div style={styles.formContainer}>
            <div style={styles.waitlistBox}>
              <h2 style={styles.formTitle}>You're on the waitlist</h2>
              <p style={styles.waitlistText}>
                We're not yet open in your area, but we've added <strong>{waitlistedEmail}</strong> to the waitlist.
              </p>
              <p style={styles.waitlistText}>
                You'll be the first to know when we expand to your metro.
              </p>
              <button
                type="button"
                onClick={() => navigate('/')}
                style={styles.submitBtn}
              >
                Back to home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Left Panel */}
      <div style={{...styles.leftPanel, display: isMobile ? 'none' : 'flex'}} className="register-left">
        <div style={styles.leftContent}>
          <Link to="/" style={styles.backLink}>← Back to home</Link>
          <div style={styles.leftBadge}>Founding Member pricing — first 24 paid only</div>
          <h1 style={styles.leftTitle}>
            {userType === 'business'
              ? 'Start winning more jobs today.'
              : 'Get competing quotes in minutes.'}
          </h1>
          <p style={styles.leftSubtitle}>
            {userType === 'business'
              ? 'Join QuoteBid and get matched to customers who need exactly what you do.'
              : 'Post your project and let local pros compete for your business.'}
          </p>
          <div style={styles.benefitsList}>
            {(userType === 'business' ? businessBenefits : customerBenefits).map((benefit, i) => (
              <div key={i} style={styles.benefit}>{benefit}</div>
            ))}
          </div>
          {userType === 'business' && (
            <div style={styles.pricingTeaser}>
              <div style={styles.pricingTeaserTitle}>Founding Member Pricing</div>
              <div style={styles.pricingTeaserRow}>
                <span>Pro</span>
                <span><s style={{ color: 'rgba(255,255,255,0.4)' }}>$249</s> <strong>$124.50</strong>/mo for life</span>
              </div>
              <div style={styles.pricingTeaserRow}>
                <span>Premium</span>
                <span><s style={{ color: 'rgba(255,255,255,0.4)' }}>$399</s> <strong>$199.50</strong>/mo for life</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel */}
      <div style={styles.rightPanel} className="register-right">
        <div style={styles.formContainer}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Create your account</h2>
            <p style={styles.formSubtitle}>
              Already have one? <Link to="/login" style={styles.formLink}>Sign in</Link>
            </p>
          </div>

          <div style={styles.typeToggle}>
            <button
              type="button"
              style={{ ...styles.typeBtn, ...(userType === 'customer' ? styles.typeBtnActive : {}) }}
              onClick={() => setUserType('customer')}
            >
              I Need a Service
            </button>
            <button
              type="button"
              style={{ ...styles.typeBtn, ...(userType === 'business' ? styles.typeBtnActive : {}) }}
              onClick={() => setUserType('business')}
            >
              I'm a Business
            </button>
          </div>

          {error && <div style={styles.errorBox}>⚠️ {error}</div>}

          {planLimitMsg && (
            <div style={styles.planLimitNotice}>{planLimitMsg}</div>
          )}

          {lanePairs && lanePairs.length > 0 && (
            <div style={styles.laneNotice}>
              <div style={styles.laneNoticeTitle}>Some of your lanes aren't available:</div>
              <ul style={styles.laneList}>
                {lanePairs.map((pair, i) => (
                  <li key={i} style={styles.laneItem}>
                    <strong>{failingPairLabel(pair, formatCategory)}</strong> — {laneReasonText(pair.reason)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>{userType === 'business' ? 'Your Name' : 'Full Name'}</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required style={styles.input} placeholder={userType === 'business' ? 'John Smith' : 'Your full name'} />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Email Address</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required style={styles.input} placeholder="you@example.com" />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Password</label>
              <input type="password" name="password" value={formData.password} onChange={handleChange} required style={styles.input} placeholder="Create a strong password" />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Phone Number</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} style={styles.input} placeholder="(555) 000-0000" />
            </div>

            {userType === 'business' && (
              <>
                <div style={styles.sectionDivider}>
                  <span style={styles.sectionDividerText}>Business Information</span>
                </div>

                {initialTier === null && (
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>
                      Choose your plan <span style={styles.required}>*</span>
                    </label>
                    <p style={styles.helperText}>You can change plans later from Settings.</p>
                    <div style={styles.tierPickerGrid}>
                      {([
                        { id: 'starter', name: 'Starter', price: 'Free', sub: '3 quotes/month' },
                        { id: 'pro', name: 'Pro', price: '$249/mo', sub: 'Unlimited quotes' },
                        { id: 'premium', name: 'Premium', price: '$399/mo', sub: 'Notified first' },
                      ] as const).map(opt => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setTier(opt.id)}
                          style={{
                            ...styles.tierPickerCard,
                            ...(tier === opt.id ? styles.tierPickerCardSelected : {}),
                          }}
                        >
                          <div style={styles.tierPickerName}>{opt.name}</div>
                          <div style={styles.tierPickerPrice}>{opt.price}</div>
                          <div style={styles.tierPickerSub}>{opt.sub}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Business Name</label>
                  <input type="text" name="business_name" value={formData.business_name} onChange={handleChange} required style={styles.input} placeholder="Your business name" />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Street Address</label>
                  <input type="text" name="address" value={formData.address} onChange={handleChange} style={styles.input} placeholder="123 Main St" />
                </div>

                <div style={styles.threeCol}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>City</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} style={styles.input} placeholder="Baton Rouge" />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>State</label>
                    <input type="text" name="state" value={formData.state} onChange={handleChange} style={styles.input} placeholder="LA" />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>ZIP</label>
                    <input type="text" name="zip" value={formData.zip} onChange={handleChange} style={styles.input} placeholder="70801" />
                  </div>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Business Description <span style={styles.optional}>(optional)</span></label>
                  <textarea name="description" value={formData.description} onChange={handleChange} style={styles.textarea} placeholder="Tell customers what services you offer..." />
                </div>

                {tier && (
                  <p style={styles.tierFootprintNote}>
                    {TIER_NAMES[tier]} includes up to {maxCategories} categories and {maxCities} launch cities.
                  </p>
                )}

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>
                    Service Categories <span style={styles.required}>*</span>
                    <span style={styles.optional}> ({categories.length} / {maxCategories} selected)</span>
                  </label>
                  {categories.length === 0 && (
                    <p style={styles.helperText}>Select at least one service your business offers.</p>
                  )}
                  {categoriesOverLimit && (
                    <p style={styles.overLimitText}>
                      Your {tier ? TIER_NAMES[tier] : ''} plan allows up to {maxCategories} categories. Remove {categories.length - maxCategories} to continue.
                    </p>
                  )}
                  <div style={styles.categoryGrid}>
                    {CATEGORY_OPTIONS.map(cat => {
                      const isSelected = categories.includes(cat);
                      const isDisabled = !isSelected && categories.length >= maxCategories;
                      return (
                        <div
                          key={cat}
                          onClick={() => toggleCategory(cat)}
                          style={{
                            ...styles.categoryChip,
                            backgroundColor: isSelected ? '#3b82f6' : (isDisabled ? '#e5e7eb' : '#f1f5f9'),
                            color: isSelected ? '#ffffff' : (isDisabled ? '#94a3b8' : '#334155'),
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          {formatCategory(cat)}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>
                    Cities You Serve <span style={styles.required}>*</span>
                    <span style={styles.optional}> ({serviceCities.length} / {maxCities} selected)</span>
                  </label>
                  {serviceCities.length === 0 && (
                    <p style={styles.helperText}>Select at least one city you serve.</p>
                  )}
                  {citiesOverLimit && (
                    <p style={styles.overLimitText}>
                      Your {tier ? TIER_NAMES[tier] : ''} plan allows up to {maxCities} cities. Remove {serviceCities.length - maxCities} to continue.
                    </p>
                  )}
                  <div style={styles.categoryGrid}>
                    {BATON_ROUGE_METRO.map(city => {
                      const isSelected = serviceCities.includes(city);
                      const isDisabled = !isSelected && serviceCities.length >= maxCities;
                      return (
                        <div
                          key={city}
                          onClick={() => toggleServiceCity(city)}
                          style={{
                            ...styles.categoryChip,
                            backgroundColor: isSelected ? '#3b82f6' : (isDisabled ? '#e5e7eb' : '#f1f5f9'),
                            color: isSelected ? '#ffffff' : (isDisabled ? '#94a3b8' : '#334155'),
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          {city}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div style={styles.consentBox}>
              <label style={styles.consentLabel}>
                <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={styles.checkbox} />
                <span style={styles.consentText}>
                  I agree to the QuoteBid <Link to="/terms" target="_blank" style={styles.formLink}>Terms of Service</Link> and <Link to="/privacy" target="_blank" style={styles.formLink}>Privacy Policy</Link><span style={{ color: '#ef4444' }}> *</span>
                </span>
              </label>
            </div>

            <div style={styles.consentBox}>
              <label style={styles.consentLabel}>
                <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} style={styles.checkbox} />
                <span style={styles.consentText}>
                  I agree to receive SMS notifications about account activity and new quotes. Message & data rates may apply. Reply STOP to opt out. <span style={{ color: '#94a3b8' }}>(Optional)</span>
                </span>
              </label>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <ReCAPTCHA
                sitekey="6LdxjHUsAAAAAC8jWs9xt-shcTVNtFSGQlrsaKND"
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !termsAccepted || !captchaToken || (userType === 'business' && (tier === null || categories.length === 0 || serviceCities.length === 0 || footprintOverLimit))}
              style={{
                ...styles.submitBtn,
                opacity: (!termsAccepted || !captchaToken) ? 0.5 : 1,
                cursor: (!termsAccepted || !captchaToken) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Creating account...' : userType === 'business' ? 'Create Business Account →' : 'Create Account →'}
            </button>

            {userType === 'business' && (
              <>
                <p style={styles.foundingNote}>
                  Founding Member pricing — 50% off Pro or Premium for the life of your active subscription, applied automatically at checkout. Limited to the first 24 paid Founding Members. Canceling forfeits the Founding Member discount.
                </p>
                <FoundingMemberCounter tone="onLight" />
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif" },
  leftPanel: { width: '420px', flexShrink: 0, background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f4c75 100%)', padding: '48px 40px', flexDirection: 'column', justifyContent: 'flex-start' },
  leftContent: { maxWidth: '340px' },
  backLink: { color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '13px', marginBottom: '32px', display: 'block' },
  leftBadge: { display: 'inline-block', background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 191, 36, 0.1))', border: '1px solid rgba(251, 191, 36, 0.4)', color: '#fbbf24', padding: '6px 16px', borderRadius: '100px', fontSize: '12px', fontWeight: 600, marginBottom: '24px' },
  leftTitle: { fontSize: '32px', fontWeight: 800, color: '#ffffff', lineHeight: 1.2, marginBottom: '16px', letterSpacing: '-0.02em' },
  leftSubtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, marginBottom: '32px' },
  benefitsList: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' },
  benefit: { fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 },
  pricingTeaser: { background: 'rgba(255,255,255,0.07)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(255,255,255,0.1)' },
  pricingTeaserTitle: { fontSize: '12px', fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' },
  pricingTeaserRow: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '8px' },
  rightPanel: { flex: 1, background: '#f8fafc', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 24px', overflowY: 'auto' },
  formContainer: { width: '100%', maxWidth: '520px' },
  formHeader: { marginBottom: '28px' },
  formTitle: { fontSize: '28px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '8px' },
  formSubtitle: { fontSize: '14px', color: '#64748b' },
  formLink: { color: '#3b82f6', textDecoration: 'none', fontWeight: 600 },
  typeToggle: { display: 'flex', gap: '6px', marginBottom: '28px', background: '#e2e8f0', borderRadius: '12px', padding: '4px' },
  typeBtn: { flex: 1, padding: '10px 16px', border: 'none', borderRadius: '8px', background: 'transparent', color: '#64748b', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
  typeBtnActive: { background: '#ffffff', color: '#0f172a', fontWeight: 700, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', marginBottom: '20px' },
  fieldGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' },
  optional: { color: '#94a3b8', fontWeight: 400 },
  input: { width: '100%', padding: '12px 14px', fontSize: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#ffffff', color: '#0f172a', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '12px 14px', fontSize: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#ffffff', color: '#0f172a', outline: 'none', boxSizing: 'border-box', minHeight: '80px', resize: 'vertical' as const },
  threeCol: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' },
  sectionDivider: { display: 'flex', alignItems: 'center', margin: '8px 0 20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' },
  sectionDividerText: { fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' },
  consentBox: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '12px' },
  consentLabel: { display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' },
  checkbox: { marginTop: '2px', width: '16px', height: '16px', flexShrink: 0, accentColor: '#3b82f6' },
  consentText: { fontSize: '13px', color: '#64748b', lineHeight: 1.5 },
  submitBtn: { width: '100%', padding: '15px 24px', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#ffffff', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 700, boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)', marginBottom: '16px' },
  foundingNote: { textAlign: 'center' as const, fontSize: '13px', color: '#64748b', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 16px', margin: 0 },
  required: { color: '#dc2626', fontWeight: 600 },
  helperText: { fontSize: '12px', color: '#94a3b8', marginTop: '-2px', marginBottom: '8px' },
  tierFootprintNote: { fontSize: '13px', color: '#475569', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', margin: '0 0 16px' },
  overLimitText: { fontSize: '12px', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', marginTop: '-2px', marginBottom: '8px' },
  planLimitNotice: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', marginBottom: '20px', lineHeight: 1.5 },
  laneNotice: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', marginBottom: '20px' },
  laneNoticeTitle: { fontWeight: 600, marginBottom: '6px' },
  laneList: { margin: 0, paddingLeft: '18px' },
  laneItem: { marginBottom: '4px', lineHeight: 1.4 },
  categoryGrid: { display: 'flex', flexWrap: 'wrap' as const, gap: '8px' },
  categoryChip: {
    padding: '8px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: 500 as const,
    transition: 'all 0.2s',
    userSelect: 'none' as const,
  },
  waitlistBox: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  waitlistText: { fontSize: '15px', color: '#475569', lineHeight: 1.6, marginBottom: '16px' },
  tierPickerGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' },
  tierPickerCard: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px 12px', textAlign: 'center' as const, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column' as const, alignItems: 'center' as const, gap: '4px' },
  tierPickerCardSelected: { border: '2px solid #3b82f6', background: '#eff6ff', boxShadow: '0 0 0 1px #3b82f6' },
  tierPickerName: { fontSize: '14px', fontWeight: 700, color: '#0f172a' },
  tierPickerPrice: { fontSize: '13px', fontWeight: 600, color: '#3b82f6' },
  tierPickerSub: { fontSize: '11px', color: '#94a3b8' },
};

export default Register;
